import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { Player, Team, saveTeam, getMatch } from '../services/matchService';
import { db } from '../config/firebase';

const TeamConfigPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const matchId = searchParams.get('matchId');
  const [teamName, setTeamName] = useState('');
  const [players, setPlayers] = useState<Player[]>([]);
  const [currentPlayer, setCurrentPlayer] = useState<Partial<Player>>({
    number: 1
  });
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [numberInput, setNumberInput] = useState<string>('1');
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkText, setBulkText] = useState('');

  // Pas de limite prédéfinie pour les numéros

  // Renvoie le plus petit numéro libre >= 1
  const getNextFreeNumber = (numbers: number[]) => {
    const taken = new Set(numbers.filter(n => Number.isInteger(n) && n > 0));
    let n = 1;
    while (taken.has(n)) n++;
    return n;
  };

  // Charger les données de l'équipe si on a un matchId
  useEffect(() => {
    const loadTeamData = async () => {
      if (matchId) {
        try {
          console.log('Chargement des données pour le match:', matchId);
          const match = await getMatch(matchId);
          
          if (match && match.teamA) {
            console.log('Données de l\'\u00e9quipe A trouvées:', match.teamA);
            // Charger le nom de l'équipe
            setTeamName(match.teamA.name || '');
            
            // Charger les joueurs
            if (match.teamA.players && match.teamA.players.length > 0) {
              setPlayers(match.teamA.players);
              
              // Déterminer le plus petit numéro disponible pour le nouveau joueur
              const nextNumber = getNextFreeNumber(match.teamA.players.map(p => p.number));
              setCurrentPlayer(prev => ({ ...prev, number: nextNumber }));
              setNumberInput(nextNumber.toString());
            }
          } else {
            console.log('Aucune donnée d\'\u00e9quipe trouvée, utilisation des valeurs par défaut');
            // Si pas de données d'équipe, utiliser les valeurs du localStorage
            const storedTeamAName = localStorage.getItem('teamAName');
            if (storedTeamAName) {
              setTeamName(storedTeamAName);
            }
          }
        } catch (error) {
          console.error('Erreur lors du chargement des données de l\'\u00e9quipe:', error);
          alert('Impossible de charger les données de l\'\u00e9quipe. Veuillez réessayer.');
        }
      } else {
        // Si pas de matchId, utiliser les valeurs du localStorage
        const storedTeamAName = localStorage.getItem('teamAName');
        if (storedTeamAName) {
          setTeamName(storedTeamAName);
        }
      }
    };
    
    loadTeamData();
  }, [matchId]);

  const handleAddPlayer = () => {
    // Validation
    if (!currentPlayer.firstName || !currentPlayer.lastName || !currentPlayer.number) {
      alert('Veuillez remplir tous les champs');
      return;
    }

    // Vérifier les doublons de numéro (sauf pour le joueur en cours d'édition)
    if (players.some((p, index) => p.number === currentPlayer.number && index !== editingIndex)) {
      alert('Ce numéro est déjà utilisé');
      return;
    }

    if (editingIndex !== null) {
      // Mise à jour d'un joueur existant
      const updatedPlayers = [...players];
      updatedPlayers[editingIndex] = currentPlayer as Player;
      setPlayers(updatedPlayers);
      setEditingIndex(null);
    } else {
      // Ajout d'un nouveau joueur
      setPlayers([...players, currentPlayer as Player]);
    }

    // Suggérer le plus petit numéro libre
    const usedNumbers = players.map(p => p.number);
    const nextNumber = getNextFreeNumber(usedNumbers);

    // Réinitialiser le formulaire avec le prochain numéro suggéré
    setCurrentPlayer({
      firstName: '',
      lastName: '',
      number: nextNumber
    });
    setNumberInput(nextNumber.toString());
  };

  const handleRemovePlayer = (index: number) => {
    const newPlayers = players.filter((_, i) => i !== index);
    setPlayers(newPlayers);
    if (editingIndex === index) {
      setEditingIndex(null);
      // Réinitialiser le formulaire
      const nextNumber = getNextFreeNumber(newPlayers.map(p => p.number));
      setCurrentPlayer({
        firstName: '',
        lastName: '',
        number: nextNumber
      });
      setNumberInput(nextNumber.toString());
    }
  };

  const handleEditPlayer = (index: number) => {
    const playerToEdit = players[index];
    setCurrentPlayer({ ...playerToEdit });
    setNumberInput(playerToEdit.number.toString());
    setEditingIndex(index);
    // Faire défiler jusqu'au formulaire d'édition
    document.getElementById('add-player-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    // Réinitialiser le formulaire
    const nextNumber = getNextFreeNumber(players.map(p => p.number));
    setCurrentPlayer({
      firstName: '',
      lastName: '',
      number: nextNumber
    });
    setNumberInput(nextNumber.toString());
  };

  // Import en masse depuis une liste collée (un joueur par ligne, "Prénom Nom")
  const handleBulkImport = () => {
    if (!bulkText.trim()) {
      alert('Collez une liste de joueurs (un par ligne).');
      return;
    }

    const lines = bulkText
      .split(/\r?\n/)
      .map(l => l.trim())
      .filter(l => l.length > 0);

    if (lines.length === 0) {
      alert('Aucune ligne valide détectée.');
      return;
    }

    const usedNumbers = new Set(players.map(p => p.number));
    const newPlayers: Player[] = [];
    for (const line of lines) {
      // Supporte prénoms composés: dernier mot = nom, le reste = prénom
      const parts = line.replace(/\s+/g, ' ').trim().split(' ');
      const lastName = parts.length > 1 ? parts[parts.length - 1] : '';
      const firstName = parts.length > 1 ? parts.slice(0, -1).join(' ') : parts[0];
      // Trouver le plus petit numéro libre tenant compte des nouveaux en cours d'ajout
      let candidate = 1;
      while (usedNumbers.has(candidate)) candidate++;
      usedNumbers.add(candidate);
      newPlayers.push({ firstName, lastName, number: candidate });
    }

    setPlayers(prev => [...prev, ...newPlayers]);
    // Réinitialise et ferme le panneau d'import
    setShowBulkImport(false);
    setBulkText('');

    // Met à jour le prochain numéro suggéré dans le formulaire individuel
    const nextAfterImport = getNextFreeNumber(Array.from(usedNumbers));
    setCurrentPlayer({ firstName: '', lastName: '', number: nextAfterImport });
    setNumberInput(String(nextAfterImport));
  };

  const handleSaveTeam = async () => {
    try {
      // Validation
      if (!teamName) {
        alert('Veuillez saisir le nom de l\'équipe');
        return;
      }

      if (players.length === 0) {
        alert('Veuillez ajouter au moins un joueur');
        return;
      }

      const teamData: Team = {
        name: teamName,
        players: players
      };

      // Sauvegarder l'équipe
      const teamId = await saveTeam(teamData);
      console.log('[TEAM CONFIG] Équipe sauvegardée dans teams, id:', teamId);

      // Si on a un matchId, mettre à jour le match avec l'ID de l'équipe A
      if (matchId) {
        const match = await getMatch(matchId);
        if (match) {
          const matchRef = doc(db, 'matches', matchId);
          try {
            await updateDoc(matchRef, {
              'teamA.id': teamId,
              'teamA.players': players
            });
            console.log('[TEAM CONFIG] Match mis à jour avec teamA:', matchId);
          } catch (e) {
            console.error('[TEAM CONFIG] Échec update match (teamA):', e);
            throw e;
          }
        }
      }

      // Rediriger vers la configuration de l'équipe adverse avec le matchId
      if (matchId) {
        navigate(`/config-equipe-adverse?matchId=${matchId}`);
      } else {
        navigate('/matches');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de l\'équipe:', error);
      alert('Impossible de sauvegarder l\'équipe. Veuillez réessayer.');
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">
        {matchId ? 'Modification de l\'\u00c9quipe' : 'Configuration de l\'\u00c9quipe'}
      </h1>

      {/* Nom de l'équipe */}
      <div className="mb-6 relative border-2 border-gray-200 rounded-lg p-6 pt-10">
        <div className="absolute -top-5 -left-2 bg-blue-600 text-white text-2xl font-bold rounded-full w-10 h-10 flex items-center justify-center shadow-lg">
          1
        </div>
        <label className="block text-gray-700 text-lg font-bold mb-2">
          Nom de l'équipe
        </label>
        <input
          type="text"
          value={teamName}
          onChange={(e) => setTeamName(e.target.value)}
          className="w-full p-2 border rounded"
          placeholder="Entrez le nom de l'équipe"
        />
      </div>

      {/* Formulaire d'ajout/édition de joueur */}
      <div id="add-player-form" className="mb-6 p-6 bg-gray-50 rounded-lg relative border-2 border-gray-200 pt-10">
        <div className="absolute -top-5 -left-2 bg-blue-600 text-white text-2xl font-bold rounded-full w-10 h-10 flex items-center justify-center shadow-lg">
          2
        </div>
        <h2 className="text-xl font-semibold mb-4">
          {editingIndex !== null ? 'Modifier le joueur' : 'Ajouter un joueur'}
        </h2>
        {/* Actions rapides */}
        <div className="flex flex-wrap gap-3 mb-4">
          <button
            onClick={() => setShowBulkImport(v => !v)}
            className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600"
          >
            {showBulkImport ? 'Fermer l\'import' : 'Importer depuis une liste'}
          </button>
        </div>

        {showBulkImport && (
          <div className="mb-6 border rounded-lg p-4 bg-white">
            <p className="text-sm text-gray-700 mb-2">
              Collez une liste de joueurs, un par ligne, au format <strong>Prénom Nom</strong> (ex: "Kylian Mbappé").
              Les numéros seront attribués automatiquement à la suite des joueurs existants.
            </p>
            <textarea
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              className="w-full p-3 border rounded h-40 font-mono text-sm"
              placeholder={"Exemple:\nMike Maignan\nThéo Hernandez\nDayot Upamecano\nAntoine Griezmann"}
            />
            <div className="flex gap-3 mt-3">
              <button
                onClick={handleBulkImport}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
              >
                Importer les joueurs
              </button>
              <button
                onClick={() => { setShowBulkImport(false); setBulkText(''); }}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Annuler
              </button>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Prénom
            </label>
            <input
              type="text"
              value={currentPlayer.firstName || ''}
              onChange={(e) => setCurrentPlayer({ ...currentPlayer, firstName: e.target.value })}
              className="w-full p-2 border rounded"
              placeholder="Prénom"
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Nom
            </label>
            <input
              type="text"
              value={currentPlayer.lastName || ''}
              onChange={(e) => setCurrentPlayer({ ...currentPlayer, lastName: e.target.value })}
              className="w-full p-2 border rounded"
              placeholder="Nom"
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Numéro
            </label>
            <input
              type="number"
              min="1"
              value={numberInput}
              onChange={(e) => {
                const value = e.target.value;
                setNumberInput(value);
                if (value && parseInt(value) > 0) {
                  setCurrentPlayer({ ...currentPlayer, number: parseInt(value) });
                }
              }}
              className="w-full p-2 border rounded"
              placeholder="Numéro du joueur"
            />
            {currentPlayer.number && players.some((p, idx) => p.number === currentPlayer.number && idx !== editingIndex) && (
              <p className="text-red-500 text-xs mt-1">Ce numéro est déjà utilisé</p>
            )}
          </div>
        </div>
        <div className="flex space-x-4 mt-4">
          <button
            onClick={handleAddPlayer}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 flex-1"
          >
            {editingIndex !== null ? 'Mettre à jour' : 'Ajouter le joueur'}
          </button>
          {editingIndex !== null && (
            <button
              onClick={cancelEdit}
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
            >
              Annuler
            </button>
          )}
        </div>
      </div>

      {/* Liste des joueurs */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-4">Joueurs ({players.length})</h2>
        <div className="space-y-2">
          {players.map((player, index) => (
            <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded border border-gray-200">
              <span className="font-medium">
                <span className="inline-block w-8 text-center bg-gray-200 rounded-full mr-2">
                  {player.number}
                </span>
                {player.firstName} {player.lastName}
              </span>
              <div className="space-x-2">
                <button
                  onClick={() => handleEditPlayer(index)}
                  className="text-blue-500 hover:text-blue-700 px-2 py-1 rounded hover:bg-blue-50"
                  title="Modifier"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleRemovePlayer(index)}
                  className="text-red-500 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50"
                  title="Supprimer"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bouton de sauvegarde */}
      <div className="relative border-2 border-gray-200 rounded-lg p-4">
        <div className="absolute -top-5 -left-2 bg-blue-600 text-white text-2xl font-bold rounded-full w-10 h-10 flex items-center justify-center shadow-lg">
          3
        </div>
        <button
          onClick={handleSaveTeam}
          className="w-full bg-green-500 text-white py-4 rounded-lg hover:bg-green-600 font-bold text-lg"
        >
          {matchId ? 'Mettre à jour l\'\u00e9quipe' : 'Sauvegarder l\'\u00e9quipe'}
        </button>
      </div>
    </div>
  );
};

export default TeamConfigPage;
